import * as React from 'react';
import { useState, useEffect } from 'react';
import { MessageBarType } from '@fluentui/react';
import { extractEmailData, downloadAttachments, showNotification } from '../../services/officeService';
import { apiClient } from '../../services/apiClient';
import UnauthorizedAccess, { ErrorType } from './UnauthorizedAccess';
import './App.css';

interface EmailInfo {
  subject: string;
  senderEmail: string;
  senderName: string;
  receivedDate: Date;
  attachmentCount: number;
  isOutgoing?: boolean;
}

interface AttachmentInfo {
  id: string;
  name: string;
  size: number;
  selected: boolean;
}

interface SharePointFolder {
  name: string;
  path: string;
  webUrl?: string;
}

interface Client {
  id: number;
  klantnaam: string;
  klantnummer: string;
  email_algemeen?: string;
  telefoon_algemeen?: string;
  status?: string;
  isactive?: number;
  created_at?: string;
}

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailInfo, setEmailInfo] = useState<EmailInfo | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [sharepointFolderPath, setSharepointFolderPath] = useState<string>('');
  const [isLoadingSharePoint, setIsLoadingSharePoint] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [availableFolders, setAvailableFolders] = useState<SharePointFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [customFolderPath, setCustomFolderPath] = useState<string>('');
  const [showFolderPicker, setShowFolderPicker] = useState<boolean>(false);
  const [isOutgoingEmail, setIsOutgoingEmail] = useState<boolean>(false);
  const [saveEmailChecked, setSaveEmailChecked] = useState<boolean>(true);
  const [saveAttachmentsChecked, setSaveAttachmentsChecked] = useState<boolean>(true);
  const [isMessageFading, setIsMessageFading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<{ type: ErrorType; message: string } | null>(null);

  /**
   * Initialize authentication and Graph client on mount
   */
  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * Initialize - load email data when component mounts
   */
  useEffect(() => {
    if (isAuthInitialized) {
      loadEmailData();
    }
  }, [isAuthInitialized]);

  /**
   * Auto-dismiss message banner after 3 seconds
   */
  useEffect(() => {
    if (message) {
      setIsMessageFading(false);
      const fadeTimer = setTimeout(() => {
        setIsMessageFading(true);
      }, 2700); // Start fade 300ms before removal

      const removeTimer = setTimeout(() => {
        setMessage(null);
        setIsMessageFading(false);
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [message]);

  /**
   * Fetch SharePoint folder path when selected client changes
   */
  useEffect(() => {
    const fetchSharePointPath = async () => {
      // Only fetch SharePoint data after authentication is initialized
      if (selectedClientId && isAuthInitialized) {
        // Clear previous folder selection when client changes
        setSelectedFolder('');
        setSharepointFolderPath('');
        setCustomFolderPath('');
        setShowFolderPicker(false);
        setIsLoadingSharePoint(true);
        
        try {
          // Fetch available folders for the client
          const foldersResult = await apiClient.getClientSharePointFolders(selectedClientId);
          if (foldersResult.success && foldersResult.folders) {
            setAvailableFolders(foldersResult.folders);
            // Set default folder (most recent or root)
            if (foldersResult.folders.length > 0) {
              setSelectedFolder(foldersResult.folders[0].path);
              setSharepointFolderPath(foldersResult.folders[0].webUrl || foldersResult.folders[0].path);
            }
          }
          
          // Also get default path (as fallback if no folders were found)
          const result = await apiClient.getClientSharePointPath(selectedClientId);
          if (result.success) {
            // Only use default path if we didn't get folders above
            if (!foldersResult.folders || foldersResult.folders.length === 0) {
              setSharepointFolderPath(result.webUrl || result.folderPath);
              setSelectedFolder(result.folderPath);
            }
            
            // Show info message if folder was created
            if (result.created) {
              setMessage({
                text: `SharePoint map aangemaakt: ${result.folderPath}`,
                type: MessageBarType.info,
              });
            }
          }
        } catch (error: any) {
          console.error('Error fetching SharePoint path:', error);
          setSharepointFolderPath('');
          setMessage({
            text: 'Waarschuwing: SharePoint map kon niet worden opgehaald',
            type: MessageBarType.warning,
          });
        } finally {
          setIsLoadingSharePoint(false);
        }
      } else {
        setSharepointFolderPath('');
        setAvailableFolders([]);
        setSelectedFolder('');
        setCustomFolderPath('');
        setShowFolderPicker(false);
        setIsLoadingSharePoint(false);
      }
    };

    fetchSharePointPath();
  }, [selectedClientId, isAuthInitialized]);

  /**
   * Initialize authentication using Azure AD SSO
   * This replaces the old JWT token authentication
   */
  const initializeAuth = async () => {
    try {
      console.log('Initializing Azure AD authentication...');
      
      // Clear any previous auth errors
      setAuthError(null);
      
      // Initialize authentication using Azure AD SSO
      const authResult = await apiClient.initializeAuth();
      console.log('Authentication result:', authResult);
      if (authResult.success) {
        console.log('✓ Azure AD authentication successful');
        setIsAuthInitialized(true);
        
        // Initialize Microsoft Graph client (for SharePoint operations)
        try {
          const graphResult = await apiClient.initializeGraphClient();
          
          if (graphResult.success) {
            console.log('✓ Microsoft Graph client initialized');
          } else {
            console.warn('Graph client initialization failed:', graphResult.message);
            // Continue anyway - Graph is only needed for SharePoint operations
          }
        } catch (graphError: any) {
          // If graph initialization throws an auth error, treat it as overall auth failure
          console.error('Graph client auth error:', graphError);
          setIsAuthInitialized(false);
          
          const errorType: ErrorType = graphError.type || 'AUTH_FAILED';
          const errorMessage = graphError.message || 'Failed to authenticate with Microsoft Graph. Please try again.';
          
          setAuthError({
            type: errorType,
            message: errorMessage
          });
          return; // Exit early to prevent further processing
        }
      } else {
        console.error('Azure AD authentication failed:', authResult.error);
        setIsAuthInitialized(false);
        
        // Set auth error for structured error handling
        const errorMsg = authResult.error || 'Authentication failed. Please ensure you are signed in to Outlook with your organization account.';
        setAuthError({
          type: 'AUTH_FAILED',
          message: errorMsg
        });
      }
    } catch (error: any) {
      console.error('Error initializing authentication:', error);
      setIsAuthInitialized(false);
      
      // Determine error type based on error properties
      let errorType: ErrorType = 'AUTH_FAILED';
      let errorMessage = 'Authentication failed. ';
      
      // Check if error has a type property (from apiClient or ssoService)
      if (error.type) {
        errorType = error.type;
        errorMessage = error.message;
      } else {
        // Legacy error handling for backwards compatibility
        if (error.message?.includes('not available')) {
          errorMessage += 'Office.js is not properly loaded. Please try reloading the add-in.';
          errorType = 'AUTH_FAILED';
        } else if (error.message?.includes('cancelled')) {
          errorMessage += 'You cancelled the sign-in. Please try again.';
          errorType = 'AUTH_FAILED';
        } else if (error.message?.includes('not from the authorized organization') || error.message?.includes('not authorized')) {
          errorMessage += 'This add-in is only available for authorized organization members.';
          errorType = 'FORBIDDEN';
        } else if (error.message?.includes('expired')) {
          errorMessage += 'Your session has expired. Please sign in again.';
          errorType = 'EXPIRED';
        } else if (error.message?.includes('Network')) {
          errorMessage += 'Network error. Please check your connection and try again.';
          errorType = 'NETWORK';
        } else {
          errorMessage += error.message || 'Please try reloading the add-in.';
        }
      }
      
      // Set structured auth error
      setAuthError({
        type: errorType,
        message: errorMessage
      });
    }
  };

  /**
   * Handle authentication retry from UnauthorizedAccess component
   */
  const handleAuthRetry = () => {
    setAuthError(null);
    setMessage(null);
    initializeAuth();
  };

  /**
   * Load email data from currently selected email
   */
  const loadEmailData = async (showLoader: boolean = true) => {
    if (showLoader) setIsLoading(true);
    setMessage(null);

    try {
      // Extract email metadata
      const emailData = await extractEmailData();
 
      console.log('Is outgoing email:', emailData.isOutgoing);
      
      // Determine if this is an outgoing email
      const isOutgoing = emailData.isOutgoing || false;
      setIsOutgoingEmail(isOutgoing);
      
      // For outgoing emails, don't save attachments (only email content)
      if (isOutgoing) {
        setSaveAttachmentsChecked(false);
      }
      
      console.log('Extracted email data:', emailData);
      setEmailInfo({
        subject: emailData.subject,
        senderEmail: emailData.senderEmail,
        senderName: emailData.senderName,
        receivedDate: emailData.receivedDate,
        attachmentCount: emailData.attachmentCount,
        isOutgoing: isOutgoing,
      });
      // Load attachments with selection checkboxes
      if (emailData.attachmentCount > 0) {
        const item = Office.context.mailbox.item;
        if (item && item.attachments) {
          const attachmentList: AttachmentInfo[] = item.attachments
            .filter(att => att.attachmentType === Office.MailboxEnums.AttachmentType.File)
            .map(att => ({
              id: att.id,
              name: att.name,
              size: att.size,
              selected: true, // Default: all selected
            }));
          setAttachments(attachmentList);
        }
      }

      // Match email with clients (use recipient for outgoing, sender for incoming)
      let emailToMatch = emailData.senderEmail;
      
      // For outgoing emails, extract recipient email from TO field
      if (isOutgoing && emailData.toRecipients) {
        // Extract first email from "Name <email@domain.com>" format
        const emailMatch = emailData.toRecipients.match(/<([^>]+)>/);
        if (emailMatch && emailMatch[1]) {
          emailToMatch = emailMatch[1];
        } else {
          // If no angle brackets, try to extract first email-like string
          const simpleEmailMatch = emailData.toRecipients.match(/[\w.-]+@[\w.-]+\.\w+/);
          if (simpleEmailMatch) {
            emailToMatch = simpleEmailMatch[0];
          }
        }
      }
      
      console.log('Email to match for client lookup:', emailToMatch, 'isOutgoing:', isOutgoing);
      await matchClients(emailToMatch);
    } catch (error: any) {
      console.error('Error loading email data:', error);
      setMessage({
        text: `Fout bij laden e-mail: ${error.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Match sender email with clients in the system
   */
  const matchClients = async (senderEmail: string) => {
    try {
      console.log('Matching clients for email:', senderEmail);
      const result = await apiClient.getKlantenByEmail(senderEmail);
        console.log('Match clients result:', result);
      if (result.success && result.klanten && result.klanten.length > 0) {
        setClients(result.klanten);

        // Auto-select if only one match
        if (result.klanten.length === 1) {
          setSelectedClientId(result.klanten[0].id);
          setMessage({
            text: `Klant gevonden: ${result.klanten[0].klantnaam} (${result.count} resultaat)`,
            type: MessageBarType.success,
          });
        } else {
          setMessage({
            text: `${result.count} klanten gevonden. Selecteer de juiste klant.`,
            type: MessageBarType.info,
          });
        }
      } else {
        // No matches found - load all clients for manual selection
        setMessage({
          text: 'Geen klant gevonden op basis van het e-mailadres van de contactpersoon.',
          type: MessageBarType.warning,
        });
        // await loadAllClients();
      }
    } catch (error: any) {
      console.error('Error matching clients:', error);
      setMessage({
        text: 'Fout bij zoeken naar klant. Selecteer handmatig.',
        type: MessageBarType.warning,
      });
      // await loadAllClients();
    }
  };

  /**
   * Load all clients for manual selection
   */
  const loadAllClients = async () => {
    try {
      const result = await apiClient.getClients();
      if (result.success) {
        setClients(result.clients);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  /**
   * Handle saving the email
   */
  const handleSaveEmail = async () => {
    debugger
    // Validation: No client selected
    if (!selectedClientId) {
      setMessage({
        text: 'Selecteer eerst een klant om verder te gaan.',
        type: MessageBarType.error,
      });
      return;
    }

    // Validation: Check if SharePoint folder is available (only if saving attachments)
    if (saveAttachmentsChecked && !sharepointFolderPath) {
      setMessage({
        text: 'De SharePoint-map voor deze klant is niet beschikbaar.',
        type: MessageBarType.error,
      });
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      // Extract email data only if saveEmailChecked is true
      let emailData;
      if (saveEmailChecked) {
        emailData = await extractEmailData();
        console.log('Email data to save:', emailData);
      } else {
        // Minimal email metadata for attachments-only save
        const item = Office.context.mailbox.item;
        if (!item) {
          throw new Error('Geen e-mail item gevonden');
        }
        emailData = {
          subject: item.subject || '',
          senderEmail: item.from?.emailAddress || '',
          senderName: item.from?.displayName || '',
          receivedDate: item.dateTimeCreated || new Date(),
          toRecipients: item.to?.map(r => r.emailAddress).join(', ') || '',
          ccRecipients: item.cc?.map(r => r.emailAddress).join(', ') || '',
          bodyHtml: null,
          bodyText: null,
        };
        console.log('Minimal email data for attachments:', emailData);
      }
      
      // Download attachments only if saveAttachmentsChecked is true
      let selectedAttachments: any[] = [];
      if (saveAttachmentsChecked) {
        const allAttachments = await downloadAttachments();
        selectedAttachments = allAttachments.filter(att => {
          // Match by original filename since ID may not be preserved
          return attachments.some(a => a.selected && a.name === att.originalFilename);
        });
        console.log('Selected attachments to upload:', selectedAttachments);
      } else {
        console.log('Skipping attachment download - saveAttachmentsChecked is false');
      }
      console.log('SharePoint folder path:', sharepointFolderPath);
      console.log('Custom folder path:', customFolderPath);
      const model = {
        clientId: selectedClientId,
        email: emailData,
        attachment: selectedAttachments,
        customFolder: customFolderPath || selectedFolder,
        isOutgoing: isOutgoingEmail,
        saveEmail: saveEmailChecked,
        saveAttachments: saveAttachmentsChecked,
      };
      console.log('Model to save:', model);

      // Save to backend
      const result = await apiClient.saveEmailAttachments(model);
      console.log('Save email result:', result);
      if (result.success) {
        const attachmentCount = result.attachmentCount || 0;
        const sharepointUploaded = result.sharepointUploaded || 0;
        
        // Determine appropriate success message based on what was saved
        let messageText: string;
        let messageType: MessageBarType;
        let notificationMessage: string;
        
        if (saveEmailChecked && !saveAttachmentsChecked) {
          // Email only
          messageText = 'E-mail is succesvol opgeslagen in de database.';
          messageType = MessageBarType.success;
          notificationMessage = 'E-mail succesvol opgeslagen';
        } else if (!saveEmailChecked && saveAttachmentsChecked) {
          // Attachments only
          if (sharepointUploaded === attachmentCount && attachmentCount > 0) {
            messageText = `Bijlagen (${attachmentCount}) zijn succesvol opgeslagen in SharePoint.`;
            messageType = MessageBarType.success;
            notificationMessage = 'Bijlagen succesvol opgeslagen';
          } else if (sharepointUploaded > 0) {
            messageText = `Sommige bijlagen (${sharepointUploaded}/${attachmentCount}) zijn opgeslagen.`;
            messageType = MessageBarType.warning;
            notificationMessage = 'Bijlagen gedeeltelijk opgeslagen';
          } else {
            messageText = 'Bijlagen konden niet worden geüpload naar SharePoint.';
            messageType = MessageBarType.error;
            notificationMessage = 'Fout bij uploaden bijlagen';
          }
        } else if (saveEmailChecked && saveAttachmentsChecked) {
          // Both email and attachments
          if (attachmentCount === 0) {
            messageText = 'E-mail is succesvol opgeslagen. Geen bijlagen gevonden.';
            messageType = MessageBarType.success;
            notificationMessage = 'E-mail succesvol opgeslagen';
          } else if (sharepointUploaded === attachmentCount) {
            messageText = 'E-mail en bijlagen zijn succesvol opgeslagen bij de geselecteerde klant.';
            messageType = MessageBarType.success;
            notificationMessage = 'E-mail en bijlagen succesvol opgeslagen';
          } else if (sharepointUploaded > 0 && sharepointUploaded < attachmentCount) {
            messageText = 'E-mail is opgeslagen. Sommige bijlagen konden niet worden opgeslagen in SharePoint.';
            messageType = MessageBarType.warning;
            notificationMessage = 'E-mail opgeslagen met waarschuwing';
          } else {
            messageText = 'E-mail is opgeslagen, maar bijlagen konden niet worden geüpload naar SharePoint.';
            messageType = MessageBarType.warning;
            notificationMessage = 'E-mail opgeslagen met waarschuwing';
          }
        } else {
          // This shouldn't happen due to button validation, but handle it
          messageText = 'Opslaan voltooid.';
          messageType = MessageBarType.success;
          notificationMessage = 'Succesvol opgeslagen';
        }

        setMessage({
          text: messageText,
          type: messageType,
        });

        // Show Outlook notification
        showNotification(notificationMessage, messageType === MessageBarType.error ? 'error' : 'info');

        // Reset form if success or warning, then close overlay
        if (messageType !== MessageBarType.error) {
          resetForm();
        }
      } else {
        throw new Error(result.message || 'Opslaan mislukt');
      }

    } catch (error: any) {
      console.error('Error saving email:', error);

      // Determine specific error message
      let errorMessage = 'Fout bij opslaan: ';

      if (error.response?.status === 404) {
        errorMessage = 'De SharePoint-map voor deze klant is niet beschikbaar.';
      } else if (error.message?.includes('SharePoint')) {
        errorMessage = 'Bijlagen konden niet worden geüpload naar SharePoint.';
      } else {
        errorMessage += error.message || 'Onbekende fout opgetreden';
      }

      setMessage({
        text: errorMessage,
        type: MessageBarType.error,
      });
      showNotification('Fout bij opslaan e-mail', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    resetForm();
  };

  /**
   * Reset form to initial state
   */
  const resetForm = (showLoader: boolean = false) => {
    setClients([]);
    setSelectedClientId(null);
    setNotes('');
    setSharepointFolderPath('');
    setIsLoadingSharePoint(false);
    setMessage(null);
    setAttachments([]);
    setAvailableFolders([]);
    setSelectedFolder('');
    setCustomFolderPath('');
    setShowFolderPicker(false);
    setIsOutgoingEmail(false);
    setSaveEmailChecked(true);
    setSaveAttachmentsChecked(true);
    if (showLoader) setEmailInfo(null);
    loadEmailData(showLoader);
  };

  /**
   * Toggle attachment selection
   */
  const toggleAttachment = (id: string) => {
    setAttachments(prev => prev.map(att => 
      att.id === id ? { ...att, selected: !att.selected } : att
    ));
  };

  const hasValidSharePointSite = (): boolean => {
    return !!sharepointFolderPath && sharepointFolderPath.startsWith('https://');
  };

  const isSaveDisabled = (): boolean => {
    if (!selectedClientId || isProcessing) return true;
    if (!saveEmailChecked && !saveAttachmentsChecked) return true;
    if (saveAttachmentsChecked) {
      if (!hasValidSharePointSite()) return true;
      if (!attachments.some(att => att.selected)) return true;
    }
    return false;
  };

  /**
   * Toggle all attachments
   */
  const toggleAllAttachments = () => {
    const allSelected = attachments.every(att => att.selected);
    setAttachments(prev => prev.map(att => ({ ...att, selected: !allSelected })));
  };

  /**
   * Handle folder selection change
   */
  const handleFolderChange = (folderPath: string) => {
    setSelectedFolder(folderPath);
    const folder = availableFolders.find(f => f.path === folderPath);
    if (folder) {
      setSharepointFolderPath(folder.webUrl || folder.path);
    }
  };

  /**
   * Get file icon based on file extension
   */
  const getFileIcon = (filename: string): { icon: string; className: string } => {
    const ext = filename.toLowerCase().split('.').pop();
    
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'].includes(ext || '')) {
      return { icon: 'image', className: 'icon-image' };
    } else if (['xlsx', 'xls', 'csv', 'xlsm', 'xlsb'].includes(ext || '')) {
      return { icon: 'grid_on', className: 'icon-spreadsheet' };
    } else if (['pdf'].includes(ext || '')) {
      return { icon: 'picture_as_pdf', className: 'icon-pdf' };
    } else if (['doc', 'docx', 'txt', 'rtf'].includes(ext || '')) {
      return { icon: 'description', className: 'icon-default' };
    } else {
      return { icon: 'insert_drive_file', className: 'icon-default' };
    }
  };

  /**
   * Get message banner class based on type
   */
  const getMessageClass = (type: MessageBarType): string => {
    switch (type) {
      case MessageBarType.success:
        return 'success';
      case MessageBarType.error:
        return 'error';
      case MessageBarType.warning:
        return 'warning';
      default:
        return '';
    }
  };

  // Show loading spinner for initial load
  if (isLoading) {
    return (
      <div className="app-container">
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">E-mail laden...</div>
          </div>
        </div>
      </div>
    );
  }

  // If there's an auth error, show the unauthorized screen
  if (authError) {
    return (
      <UnauthorizedAccess
        errorMessage={authError.message}
        errorType={authError.type}
        onRetry={handleAuthRetry}
      />
    );
  }

  return (
    <div className="app-container">
      {/* Header */}
      <header className="header">
        <h1 className="header-title">
          <span className="material-icons-round header-icon">link</span>
          E-mail aan klant koppelen
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Email Type Indicator */}
          {emailInfo && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '6px',
              backgroundColor: isOutgoingEmail ? 'var(--info-bg)' : 'var(--success-bg)',
              border: isOutgoingEmail ? '1px solid var(--info-border)' : '1px solid var(--success-border)',
            }}>
              <span className="material-icons-round" style={{ 
                fontSize: '16px', 
                color: isOutgoingEmail ? 'var(--info-text)' : 'var(--success-text)' 
              }}>
                {isOutgoingEmail ? 'send' : 'inbox'}
              </span>
              <span style={{ 
                fontSize: '11px', 
                fontWeight: 600,
                color: isOutgoingEmail ? 'var(--info-text)' : 'var(--success-text)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {isOutgoingEmail ? 'Verzonden' : 'Ontvangen'}
              </span>
            </div>
          )}
          <div className="brand-logo">
            <img 
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuA3r7oODoRznHNC2WWeuXzh8RiulKbj_P_YJxI8PWLWFfIPWeZ1k2_NsHZRGejL14CPEasPuT4bmgeqI8sp0l4m-26buRJEL-tGEqiOGIRw39Z_xZ6OhrVjJZ-6hgINkzOPv6rJNNjXwEDJAZi0g-8WHdA7p0EOulybFGKfhrNoyfHqn8Agcnhk1qxlAysNOTFiKmqhBYzX4zjspRSnTci0-6PmjHAf7541GBzp1EOd1LkuibiHuhAnVZ_sGUtQPTvO-9PBpr02IaSs" 
              alt="Brand Logo" 
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Message Banner */}
        {message && (
          <div className={`info-banner ${getMessageClass(message.type)} ${isMessageFading ? 'fading' : ''}`}>
            <span className="material-icons-round banner-icon">info</span>
            <div className="banner-content">
              <p className="banner-text">{message.text}</p>
            </div>
            <button className="banner-close" onClick={() => {
              setIsMessageFading(true);
              setTimeout(() => {
                setMessage(null);
                setIsMessageFading(false);
              }, 300);
            }}>
              <span className="material-icons-round">close</span>
            </button>
          </div>
        )}

        {/* Client Selection */}
        <div className="form-section">
          <label className="form-label">
            Selecteer klant <span className="required">*</span>
          </label>
          <div className="select-wrapper">
            <select
              value={selectedClientId || ''}
              onChange={(e) => setSelectedClientId(Number(e.target.value) || null)}
              disabled={isProcessing}
            >
              <option value="">Kies een klant</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.klantnaam} - {client.klantnummer}
                </option>
              ))}
            </select>
            <span className="material-icons-round select-arrow">expand_more</span>
          </div>
        </div>

        {/* SharePoint Folder Selection */}
        {selectedClientId && !isOutgoingEmail && emailInfo && emailInfo.attachmentCount > 0 && (
          hasValidSharePointSite() ? (
            <div className="form-section">
              <label className="form-label">SharePoint Map</label>
              <div className="select-wrapper">
                <select
                  value={selectedFolder || 'auto'}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'custom') {
                      setShowFolderPicker(true);
                    } else if (value === 'auto') {
                      setSelectedFolder('');
                      setCustomFolderPath('');
                    } else {
                      handleFolderChange(value);
                    }
                  }}
                  disabled={isProcessing || isLoadingSharePoint}
                >
                  <option value="auto">Automatisch (standaard structuur)</option>
                  {availableFolders.map((folder) => (
                    <option key={folder.path} value={folder.path}>
                      {folder.name}
                    </option>
                  ))}
                  <option value="custom">Aangepaste map...</option>
                </select>
                <span className="material-icons-round select-arrow">expand_more</span>
              </div>
              {showFolderPicker && (
                <input
                  type="text"
                  placeholder="Bijvoorbeeld: 2026 jan >> dec 2025"
                  value={customFolderPath}
                  onChange={(e) => setCustomFolderPath(e.target.value)}
                  disabled={isProcessing}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    marginTop: '8px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                />
              )}
              {!showFolderPicker && (
                <a
                  href={sharepointFolderPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sharepoint-folder-path"
                  style={{
                    color: 'var(--primary)',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    marginTop: '6px'
                  }}
                >
                  <span className="material-icons-round" style={{ fontSize: '14px' }}>open_in_new</span>
                  {sharepointFolderPath}
                </a>
              )}
            </div>
          ) : (
            !isLoadingSharePoint && (
              <div className="info-banner" style={{ borderLeft: '3px solid var(--warning)' }}>
                <span className="material-icons-round banner-icon" style={{ color: 'var(--warning)' }}>warning</span>
                <div className="banner-content">
                  <p className="banner-text">Er is geen SharePoint-site beschikbaar voor deze klant.</p>
                </div>
              </div>
            )
          )
        )}
        {/* Loading SharePoint */}
        {isLoadingSharePoint && selectedClientId && emailInfo && emailInfo.attachmentCount > 0 && !isOutgoingEmail && !hasValidSharePointSite() && (
          <div className="info-box">
            <div className="loading-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
            <p className="info-box-text">SharePoint mappen laden...</p>
          </div>
        )}
        {/* Save Options */}
        {selectedClientId && (
          <div className="form-section">
            <label className="form-label">Opslaan opties</label>
            <div className="checkbox-group">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={saveEmailChecked}
                  onChange={() => setSaveEmailChecked(!saveEmailChecked)}
                  disabled={isProcessing}
                />
                <span>E-mail opslaan</span>
              </label>
              <label className="checkbox-item" style={{ opacity: isOutgoingEmail ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  checked={saveAttachmentsChecked}
                  onChange={() => setSaveAttachmentsChecked(!saveAttachmentsChecked)}
                  disabled={isProcessing || attachments.length === 0 || isOutgoingEmail}
                />
                <span>Bijlagen opslaan{isOutgoingEmail && ' (niet beschikbaar voor verzonden items)'}</span>
              </label>
            </div>
            {!saveEmailChecked && !saveAttachmentsChecked && (
              <div className="info-banner warning" style={{ marginTop: '12px' }}>
                <span className="material-icons-round banner-icon">warning</span>
                <div className="banner-content">
                  <p className="banner-text">Selecteer minimaal één optie om op te slaan.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Attachments Selection */}
        {saveAttachmentsChecked && attachments.length > 0 && selectedClientId && !isOutgoingEmail && (
          <div className="form-section">
            <div className="attachments-header">
              <label className="form-label">Bijlagen selecteren</label>
              <span className="attachments-count">{attachments.length} files</span>
            </div>
            
            <label className="select-all-wrapper">
              <input
                type="checkbox"
                checked={attachments.every(att => att.selected)}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = attachments.some(att => att.selected) && !attachments.every(att => att.selected);
                  }
                }}
                onChange={toggleAllAttachments}
                disabled={isProcessing}
              />
              <span className="select-all-label">
                Alle bijlagen selecteren ({attachments.length})
              </span>
            </label>

            <div className="attachments-card">
              {attachments.map((att) => {
                const fileIconInfo = getFileIcon(att.name);
                return (
                  <div key={att.id} className="attachment-item" onClick={() => !isProcessing && toggleAttachment(att.id)}>
                    <input
                      type="checkbox"
                      className="attachment-checkbox"
                      checked={att.selected}
                      onChange={() => toggleAttachment(att.id)}
                      disabled={isProcessing}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className={`attachment-icon ${fileIconInfo.className}`}>
                      <span className="material-icons-round">{fileIconInfo.icon}</span>
                    </div>
                    <div className="attachment-info">
                      <p className="attachment-name">{att.name}</p>
                      <p className="attachment-size">{(att.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}



        {/* No Attachments Message */}
        {emailInfo && emailInfo.attachmentCount === 0 && selectedClientId && !isOutgoingEmail && (
          <div className="info-banner">
            <span className="material-icons-round banner-icon">info</span>
            <div className="banner-content">
              <p className="banner-text">Deze e-mail heeft geen bijlagen om te uploaden.</p>
            </div>
          </div>
        )}

        {/* Outgoing Email Indicator */}
        {isOutgoingEmail && (
          <div className="info-box">
            <span className="material-icons-round info-box-icon">info</span>
            <p className="info-box-text">
              Dit is een uitgaande e-mail. Deze wordt opgeslagen bij de klant.
            </p>
          </div>
        )}
      </main>

      {/* Footer Actions */}
      <footer className="footer-actions">
        <div className="button-group">
          <button
            className="btn-primary"
            onClick={handleSaveEmail}
            disabled={isSaveDisabled()}
          >
            <span className="material-icons-round">save</span>
            {isProcessing ? 'Bezig...' : 'Opslaan'}
          </button>
          <button
            className="btn-secondary"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            <span className="material-icons-round">close</span>
            Annuleren
          </button>
        </div>
      </footer>

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">E-mail wordt opgeslagen...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
